import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { usePersistedState } from "../lib/useSort";
import { Sparkles, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useIsMobile } from "../lib/useMobile";
import { renderMd } from "../lib/markdown";
import PeriodBadge from "./PeriodBadge";

function ReadMore({ text }) {
  const mobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const long = mobile && (text || "").length > 600;
  return (
    <div>
      <div
        className={`max-w-none ${long && !open ? "bm-clamp" : ""}`}
        data-testid="insights-text"
      >
        {renderMd(text, { size: "base" })}
      </div>
      {long && (
        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="insights-read-more"
          className="mt-2 min-h-[44px] px-3 bm-eyebrow border border-[#0a0a0a] bg-[#f5f2eb] flex items-center gap-1"
        >
          {open ? "Show less" : "Read more"}
        </button>
      )}
    </div>
  );
}

export default function InsightsPanel({ range, outletIds }) {
  const [history, setHistory] = useState([]); // newest first
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  // Remember My View — persist collapsed/expanded across reloads.
  const [expanded, setExpanded] = usePersistedState("bm-insights:expanded", true);

  const insight = history[idx] || null;

  const params = range
    ? {
        start_date: range.start,
        end_date: range.end,
        ...(outletIds?.length ? { outlet_ids: outletIds.join(",") } : {}),
      }
    : null;

  async function loadHistory(selectNewest = true) {
    if (!params) return;
    try {
      const { data } = await api.get("/insights/history", { params });
      const items = data?.items || [];
      setHistory(items);
      if (selectNewest) setIdx(0);
    } catch (e) {}
  }

  useEffect(() => {
    setHistory([]);
    setIdx(0);
    loadHistory();
    // eslint-disable-next-line
  }, [range?.start, range?.end, outletIds?.join(",")]);

  async function generate() {
    if (!params) return;
    setLoading(true);
    setErr("");
    try {
      await api.post("/insights/generate", null, { params });
      await loadHistory(true); // newest brief goes to the front
      setExpanded(true);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to generate");
    } finally {
      setLoading(false);
    }
  }

  const hasHistory = history.length > 0;
  const multiple = history.length > 1;
  // idx 0 is newest; larger idx = older.
  const canNewer = idx > 0;
  const canOlder = idx < history.length - 1;

  return (
    <div className="border border-[#0a0a0a] bg-[#ede8dc] p-6 lg:p-8" data-testid="insights-panel">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="bm-eyebrow text-neutral-500">The Editor's Note · Claude</div>
          <h3 className="text-lg font-semibold mt-1">What the numbers <span className="italic text-[#c72c2c]">actually</span> say</h3>
          {range && <PeriodBadge range={range} className="mt-2" />}
        </div>
        <div className="flex items-center gap-2">
          {hasHistory && (
            <button
              onClick={() => setExpanded((v) => !v)}
              data-testid="insights-toggle"
              aria-expanded={expanded}
              title={expanded ? "Hide the write-up" : "Show the write-up"}
              className="bm-eyebrow px-3 py-3 border border-[#0a0a0a] bg-[#f5f2eb] hover:bg-[#ede8dc] flex items-center gap-1.5 whitespace-nowrap"
            >
              <ChevronDown size={14} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
              {expanded ? "Hide" : "Show"}
            </button>
          )}
          <button
            onClick={generate}
            disabled={loading || !range}
            data-testid="generate-insights-button"
            className="bm-eyebrow px-4 py-3 border border-[#0a0a0a] bg-[#f5f2eb] hover:bg-[#ede8dc] disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
          >
            <Sparkles size={14} /> {loading ? "Writing…" : hasHistory ? "Rewrite brief" : "Generate brief"}
          </button>
        </div>
      </div>

      {err && <div className="mt-4 text-[#c72c2c] font-mono-bm text-sm">{err}</div>}

      {expanded && (
        <div className="mt-6 max-w-3xl">
          {!hasHistory && !loading && (
            <p className="text-neutral-600">Press <b>Generate brief</b> to have Claude read this range and write a 200-word editorial on what's moving and why.</p>
          )}
          {loading && <p className="text-neutral-600 font-mono-bm text-sm animate-pulse">Reading receipts, thinking hard…</p>}
          {insight && (
            <ReadMore text={insight.text} />
          )}
          {insight?.generated_at && (
            <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
              <div className="bm-eyebrow text-neutral-500">
                Filed {new Date(insight.generated_at).toLocaleString()}
                {idx === 0 && multiple && <span className="ml-2 text-[#c72c2c]">· latest</span>}
              </div>
              {multiple && (
                <div className="flex items-center gap-2" data-testid="insights-history-nav">
                  <button
                    onClick={() => setIdx((i) => Math.min(history.length - 1, i + 1))}
                    disabled={!canOlder}
                    data-testid="insights-history-older"
                    title="Older brief"
                    className="bm-eyebrow px-2.5 py-2 border border-[#0a0a0a] bg-[#f5f2eb] hover:bg-[#ede8dc] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <ChevronLeft size={13} /> Older
                  </button>
                  <span className="bm-eyebrow text-neutral-600 tabular-nums" data-testid="insights-history-counter">
                    {history.length - idx} / {history.length}
                  </span>
                  <button
                    onClick={() => setIdx((i) => Math.max(0, i - 1))}
                    disabled={!canNewer}
                    data-testid="insights-history-newer"
                    title="Newer brief"
                    className="bm-eyebrow px-2.5 py-2 border border-[#0a0a0a] bg-[#f5f2eb] hover:bg-[#ede8dc] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    Newer <ChevronRight size={13} />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
